/**
 * 텍스처 베이크 — 런타임 N뷰 투영을 UV 아틀라스 한 장으로 굽는다.
 *
 * Meshy 의 산출물은 "메시 + 2K/4K 베이스컬러 + PBR 맵" 한 벌이다. 우리 뷰어는 셰이더가
 * 매 프레임 여덟 뷰를 섞어 그리는데, 그건 glTF 에 담을 수 없다 — 내보낸 glb 는 정점의
 * 대표 UV 로만 그려져 뷰 경계에서 텍스처가 깨졌다. 그래서 셰이더가 하던 계산을 CPU 에서
 * 한 번 돌려 **아틀라스 텍셀마다** 섞인 색을 써 넣는다. 뷰어와 glb 가 같은 그림이 된다.
 *
 * 차트 = 뷰 칸. 삼각형마다 "가장 정면으로 보는 뷰" 하나를 고르고, 그 뷰 칸 안에 세 정점의
 * UV 를 찍는다. 정점이 다른 차트에도 속하면 복제한다(정점 분할). 그 삼각형의 텍셀들에는
 * 법선·가시성 가중 블렌드로 여덟 뷰를 섞은 색을 쓴다 — 셰이더 `MAP_FRAGMENT` 와 같은 식.
 * 차트 경계는 4 텍셀 팽창(dilate)해 밉맵이 내려가도 배경색이 새지 않게 한다.
 */

import * as THREE from 'three';
import type { Raster } from '../types.ts';
import type { ViewAtlas } from './atlas.ts';
import type { ProjectionUniforms } from './hull.ts';

export interface BakeResult {
  /** 정점이 차트별로 분할된 새 지오메트리. position·normal·uv·aSmoothN·aVisA/B·aShell 을 가진다. */
  geometry: THREE.BufferGeometry;
  /** 아틀라스 크기의 베이스컬러. 알파 = 커버리지. */
  albedo: Raster;
  /** 같은 배치로 구운 나머지 맵 (넘긴 것만). */
  extra: Record<string, Raster>;
  /** 분할 뒤 정점 수 */
  vertexCount: number;
  /** 차트별 삼각형 수 */
  chartShare: number[];
}

// 4 에서는 정면 ±45° 뷰가 얼굴에 20% 씩 섞여 눈이 넷으로 보였다(실측: 고블린). 뷰끼리 완전히 정합되지 않는 생성 이미지에서는 "가장 정면 뷰가 거의 다 가져가는" 쪽이 낫다.
const BLEND_POW = 12;
/**
 * 이음매 억제 — 텍셀마다 가장 센 뷰의 이 비율 아래인 뷰는 버린다(승자독식 + 좁은 띠).
 * 뺨처럼 법선이 두 뷰 사이(22.5°)를 지나는 곳은 pow 12 로도 반반이 섞여 눈썹이 둘로 보였다
 * (실측: 엠버 뱅가드 정면). 정합이 안 되는 생성 이미지에서 부드러운 블렌드는 곧 이중상이다 —
 * 좁은 이음매가 낫다. 0.5 면 띠 폭이 3~5° 다.
 */
const SEAM_KEEP = 0.5;
/**
 * 뷰 편향 — 정면 뷰를 세게, 후면을 조금 우대한다. 정면은 컨셉 원화라 얼굴이 가장 정확하고,
 * 정사 투영이라 헐의 깊이 오차가 샘플 위치를 흔들지 않는다(오차 = δ·sinθ, 정면은 θ=0).
 * pow 12 에 편향 24 면 정면↔45° 이음매가 22.5° → 약 40° 로 물러나 눈·눈썹이 이음매를 벗어난다.
 */
function viewBias(dz: number, front = 24): number { return dz > 0.99 ? front : dz < -0.99 ? Math.min(6, front) : 1; }

/** 베이크 조절 — 파츠(머리)는 얼굴 전체를 정면 한 장이 갖게 훨씬 세게 민다. */
export interface BakeOptions {
  /** 정면 뷰 가중치 배수. 얼굴은 헐이 코를 모르니 45° 뷰가 눈을 다른 자리에 찍는다 — 크게 줄수록 이음매가 귀 뒤로 밀린다. */
  frontBias?: number;
  /** 텍셀마다 최대 가중치의 이 비율 아래인 뷰는 버린다. 1 에 가까울수록 이음매가 얇다. */
  seamKeep?: number;
}

function bilinear(src: Raster, u: number, v: number, out: [number, number, number]) {
  // uv → 픽셀. v 는 위가 1.
  const fx = Math.min(src.width - 1, Math.max(0, u * src.width - 0.5));
  const fy = Math.min(src.height - 1, Math.max(0, (1 - v) * src.height - 0.5));
  const x0 = Math.floor(fx), y0 = Math.floor(fy);
  const x1 = Math.min(src.width - 1, x0 + 1), y1 = Math.min(src.height - 1, y0 + 1);
  const tx = fx - x0, ty = fy - y0;
  const p00 = (y0 * src.width + x0) * 4, p10 = (y0 * src.width + x1) * 4, p01 = (y1 * src.width + x0) * 4, p11 = (y1 * src.width + x1) * 4;
  for (let c = 0; c < 3; c++) {
    const a = src.data[p00 + c]! + (src.data[p10 + c]! - src.data[p00 + c]!) * tx;
    const b = src.data[p01 + c]! + (src.data[p11 + c]! - src.data[p01 + c]!) * tx;
    out[c] = a + (b - a) * ty;
  }
}

/** 아틀라스 마스크(실루엣) 샘플 — 0..1. 뷰 실루엣 밖을 가리키는 샘플은 배경색이다. */
function maskAt(mask: { width: number; height: number; data: Float32Array }, u: number, v: number): number {
  const x = Math.min(mask.width - 1, Math.max(0, Math.round(u * mask.width - 0.5)));
  const y = Math.min(mask.height - 1, Math.max(0, Math.round((1 - v) * mask.height - 0.5)));
  return mask.data[y * mask.width + x]!;
}

/**
 * 실루엣 밖 샘플 억제. 헐은 실루엣 관용(k+1)과 깎기 때문에 한두 뷰의 실루엣 **밖**에 있는 정점을
 * 가질 수 있다 — 그 뷰로 투영하면 흰 배경을 찍는다(실측: 엠버 뱅가드의 허벅지 안쪽 흰 얼룩).
 * 그런 뷰의 가중치를 이 배수로 누른다. 0 이 아닌 이유: 모든 뷰가 밖이면 그래도 가장 가까운 색을 줘야 한다.
 */
const OUTSIDE_WEIGHT = 0.02;

/**
 * @param geometry 헐 지오메트리(최종 오브젝트 좌표, aSmoothN·aVisA/B 포함)
 * @param albedoAtlas 아틀라스 공간의 albedo(디라이팅·번짐까지 끝난 맵)
 */
export function bakeTexture(
  geometry: THREE.BufferGeometry,
  albedoAtlas: Raster,
  atlas: ViewAtlas,
  projection: ProjectionUniforms,
  /**
   * 함께 구울 맵(아틀라스 공간, albedo 와 같은 크기). ORM·노멀·이미시브.
   * 이걸 안 구우면 차트 UV 가 뷰 실루엣 밖에 떨어지는 텍셀이 배경(흰색)의 ORM 을 먹는다 —
   * 흰색은 "밝고 무채색" 이라 메탈니스가 올라가 허벅지 안쪽에 회색 금속 얼룩이 섰다(실측 2026-09-04).
   */
  extraAtlas: Record<string, Raster> = {},
  opts: BakeOptions = {},
): BakeResult {
  const frontBias = opts.frontBias ?? 24;
  const seamKeep = opts.seamKeep ?? SEAM_KEEP;
  const pos = geometry.getAttribute('position') as THREE.BufferAttribute;
  const nrm = geometry.getAttribute('normal') as THREE.BufferAttribute;
  const smoothN = (geometry.getAttribute('aSmoothN') as THREE.BufferAttribute | undefined) ?? nrm;
  const visA = geometry.getAttribute('aVisA') as THREE.BufferAttribute | undefined;
  const visB = geometry.getAttribute('aVisB') as THREE.BufferAttribute | undefined;
  const index = geometry.getIndex()!;
  const n = projection.count;
  const W = albedoAtlas.width, H = albedoAtlas.height;

  // 뷰 파라미터
  const rx = new Float32Array(n), rz = new Float32Array(n), iw = new Float32Array(n), dx = new Float32Array(n), dz = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    rx[i] = projection.r[i * 4]!; rz[i] = projection.r[i * 4 + 1]!; iw[i] = projection.r[i * 4 + 2]!;
    dx[i] = -rz[i]!; dz[i] = rx[i]!;
  }
  const rects = atlas.rects;
  const off = projection.offset, sc = projection.scale;
  const bias = new Float32Array(n);
  for (let i = 0; i < n; i++) bias[i] = viewBias(dz[i]!, frontBias);

  // 헐 좌표 p = (P - offset) * scale
  const hull = new Float32Array(pos.count * 3);
  for (let v = 0; v < pos.count; v++) {
    hull[v * 3] = (pos.getX(v) - off.x) * sc;
    hull[v * 3 + 1] = (pos.getY(v) - off.y) * sc;
    hull[v * 3 + 2] = (pos.getZ(v) - off.z) * sc;
  }
  const visOf = (v: number, i: number) => {
    if (!visA || !visB) return 1;
    return i < 4 ? visA.getComponent(v, i) : visB.getComponent(v, i - 4);
  };
  const stOf = (v: number, i: number): [number, number] => [
    (hull[v * 3]! * rx[i]! + hull[v * 3 + 2]! * rz[i]!) * iw[i]! + 0.5, hull[v * 3 + 1]!,
  ];
  const uvOf = (v: number, i: number): [number, number] => {
    const [s, t] = stOf(v, i);
    const r = rects[i]!;
    return [r.u0 + Math.min(1, Math.max(0, s)) * r.du, r.v0 + Math.min(1, Math.max(0, t)) * r.dv];
  };
  const insideOf = (v: number, i: number) => {
    const [u, vv] = uvOf(v, i);
    return maskAt(atlas.mask, u, vv) >= 0.5 ? 1 : OUTSIDE_WEIGHT;
  };
  const weightOf = (v: number, i: number) => {
    const nx = smoothN.getX(v), nz = smoothN.getZ(v);
    const h = Math.hypot(nx, nz) || 1e-6;
    return Math.pow(Math.max(0, (nx / h) * dx[i]! + (nz / h) * dz[i]!), BLEND_POW) * bias[i]! * (0.03 + 0.97 * visOf(v, i)) * insideOf(v, i);
  };

  // 1) 삼각형 → 차트(뷰). 세 정점 가중치 합이 최대인 뷰.
  const triCount = index.count / 3;
  const chart = new Uint8Array(triCount);
  const chartShare = new Array<number>(n).fill(0);
  for (let t = 0; t < triCount; t++) {
    const a = index.getX(t * 3), b = index.getX(t * 3 + 1), c = index.getX(t * 3 + 2);
    let best = 0, bestW = -1;
    for (let i = 0; i < n; i++) {
      const w = weightOf(a, i) + weightOf(b, i) + weightOf(c, i);
      if (w > bestW) { bestW = w; best = i; }
    }
    chart[t] = best;
    chartShare[best]!++;
  }

  // 2) 정점 분할: (정점, 차트) 쌍마다 새 정점.
  const keyMap = new Map<number, number>();
  const newPos: number[] = [], newNrm: number[] = [], newUv: number[] = [], newSmooth: number[] = [], newVisA: number[] = [], newVisB: number[] = [];
  // Uint32 여야 한다. Int32Array 를 인덱스로 주면 three 가 gl.INT 로 올려 WebGL 이 조용히 아무것도 그리지 않는다
  // (본체는 뒤에 리메시가 인덱스를 다시 만들어 가려졌고, 리메시를 건너뛴 머리 파츠에서 드러났다 — 2026-09-04).
  const newIndex = new Uint32Array(index.count);
  const srcOf: number[] = [];
  for (let t = 0; t < triCount; t++) {
    const ci = chart[t]!;
    for (let k = 0; k < 3; k++) {
      const v = index.getX(t * 3 + k);
      const key = v * 16 + ci;
      let nv = keyMap.get(key);
      if (nv === undefined) {
        nv = newPos.length / 3;
        keyMap.set(key, nv);
        srcOf.push(v);
        newPos.push(pos.getX(v), pos.getY(v), pos.getZ(v));
        newNrm.push(nrm.getX(v), nrm.getY(v), nrm.getZ(v));
        newSmooth.push(smoothN.getX(v), smoothN.getY(v), smoothN.getZ(v));
        const [u, vv] = uvOf(v, ci);
        newUv.push(u, vv);
        for (let i = 0; i < 4; i++) newVisA.push(visOf(v, i));
        for (let i = 4; i < 8; i++) newVisB.push(visOf(v, i));
      }
      newIndex[t * 3 + k] = nv;
    }
  }

  // 3) 베이크. 차트 안에서 삼각형을 텍셀 래스터.
  const out: Raster = { width: W, height: H, data: new Uint8ClampedArray(W * H * 4) };
  const extraKeys = Object.keys(extraAtlas).filter((k) => extraAtlas[k]!.width === W && extraAtlas[k]!.height === H);
  const extraOut: Record<string, Raster> = {};
  for (const k of extraKeys) extraOut[k] = { width: W, height: H, data: new Uint8ClampedArray(W * H * 4) };
  const srcs: Raster[] = [albedoAtlas, ...extraKeys.map((k) => extraAtlas[k]!)];
  const outs: Raster[] = [out, ...extraKeys.map((k) => extraOut[k]!)];
  const written = new Uint8Array(W * H);
  const rgb: [number, number, number] = [0, 0, 0];
  const acc = new Float32Array(srcs.length * 3);
  const ws = new Float32Array(n);
  const sus = new Float32Array(n), svs = new Float32Array(n);
  for (let t = 0; t < triCount; t++) {
    const ia = newIndex[t * 3]!, ib = newIndex[t * 3 + 1]!, ic = newIndex[t * 3 + 2]!;
    const sa = srcOf[ia]!, sb = srcOf[ib]!, scv = srcOf[ic]!;
    // 텍셀 좌표 (x 오른쪽, y 아래)
    const ax = newUv[ia * 2]! * W, ay = (1 - newUv[ia * 2 + 1]!) * H;
    const bx = newUv[ib * 2]! * W, by = (1 - newUv[ib * 2 + 1]!) * H;
    const cx = newUv[ic * 2]! * W, cy = (1 - newUv[ic * 2 + 1]!) * H;
    const area = (bx - ax) * (cy - ay) - (cx - ax) * (by - ay);
    if (Math.abs(area) < 1e-6) continue;
    const inv = 1 / area;
    const x0 = Math.max(0, Math.floor(Math.min(ax, bx, cx))), x1 = Math.min(W - 1, Math.ceil(Math.max(ax, bx, cx)));
    const y0 = Math.max(0, Math.floor(Math.min(ay, by, cy))), y1 = Math.min(H - 1, Math.ceil(Math.max(ay, by, cy)));
    for (let py = y0; py <= y1; py++) {
      for (let px = x0; px <= x1; px++) {
        const qx = px + 0.5, qy = py + 0.5;
        let w0 = ((bx - qx) * (cy - qy) - (cx - qx) * (by - qy)) * inv;
        let w1 = ((cx - qx) * (ay - qy) - (ax - qx) * (cy - qy)) * inv;
        let w2 = 1 - w0 - w1;
        // 한 텍셀 여유 — 삼각형 사이 틈이 안 생기게. 밖이면 가장 가까운 안쪽으로 당긴다.
        if (w0 < -0.5 || w1 < -0.5 || w2 < -0.5) continue;
        w0 = Math.max(0, w0); w1 = Math.max(0, w1); w2 = Math.max(0, w2);
        const ws3 = w0 + w1 + w2 || 1; w0 /= ws3; w1 /= ws3; w2 /= ws3;
        const pi = py * W + px;
        if (written[pi]) continue;
        // 헐 위치·매끈 법선·가시성 보간
        const hx = w0 * hull[sa * 3]! + w1 * hull[sb * 3]! + w2 * hull[scv * 3]!;
        const hy = w0 * hull[sa * 3 + 1]! + w1 * hull[sb * 3 + 1]! + w2 * hull[scv * 3 + 1]!;
        const hz = w0 * hull[sa * 3 + 2]! + w1 * hull[sb * 3 + 2]! + w2 * hull[scv * 3 + 2]!;
        const nx = w0 * newSmooth[ia * 3]! + w1 * newSmooth[ib * 3]! + w2 * newSmooth[ic * 3]!;
        const nz = w0 * newSmooth[ia * 3 + 2]! + w1 * newSmooth[ib * 3 + 2]! + w2 * newSmooth[ic * 3 + 2]!;
        const hl = Math.hypot(nx, nz) || 1e-6;
        const hnx = nx / hl, hnz = nz / hl;
        let wsum = 0;
        for (let i = 0; i < n; i++) {
          const vis = i < 4
            ? w0 * newVisA[ia * 4 + i]! + w1 * newVisA[ib * 4 + i]! + w2 * newVisA[ic * 4 + i]!
            : w0 * newVisB[ia * 4 + i - 4]! + w1 * newVisB[ib * 4 + i - 4]! + w2 * newVisB[ic * 4 + i - 4]!;
          const s = (hx * rx[i]! + hz * rz[i]!) * iw[i]! + 0.5;
          const r = rects[i]!;
          const su = r.u0 + Math.min(1, Math.max(0, s)) * r.du, sv = r.v0 + Math.min(1, Math.max(0, hy)) * r.dv;
          sus[i] = su; svs[i] = sv;
          const inside = maskAt(atlas.mask, su, sv) >= 0.5 ? 1 : OUTSIDE_WEIGHT;
          const w = (Math.pow(Math.max(0, hnx * dx[i]! + hnz * dz[i]!), BLEND_POW) * bias[i]! + 1e-4) * (0.03 + 0.97 * vis) * inside;
          ws[i] = w;
        }
        let wmax = 0;
        for (let i = 0; i < n; i++) if (ws[i]! > wmax) wmax = ws[i]!;
        for (let i = 0; i < n; i++) { if (ws[i]! < wmax * seamKeep) ws[i] = 0; wsum += ws[i]!; }
        acc.fill(0);
        for (let i = 0; i < n; i++) {
          const w = ws[i]! / wsum;
          if (w < 1e-3) continue;
          for (let m = 0; m < srcs.length; m++) {
            bilinear(srcs[m]!, sus[i]!, svs[i]!, rgb);
            acc[m * 3] += rgb[0] * w; acc[m * 3 + 1] += rgb[1] * w; acc[m * 3 + 2] += rgb[2] * w;
          }
        }
        const o = pi * 4;
        for (let m = 0; m < outs.length; m++) {
          const d = outs[m]!.data;
          d[o] = acc[m * 3]!; d[o + 1] = acc[m * 3 + 1]!; d[o + 2] = acc[m * 3 + 2]!; d[o + 3] = 255;
        }
        written[pi] = 1;
      }
    }
  }

  // 4) 팽창 — 쓰인 텍셀 바깥으로 4 텍셀, 이웃 평균. 모든 출력 맵에 같은 마스크로.
  const sum = new Float32Array(outs.length * 3);
  for (let pass = 0; pass < 4; pass++) {
    const next = new Uint8Array(written);
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        const i = y * W + x;
        if (written[i]) continue;
        sum.fill(0); let c = 0;
        for (let oy = -1; oy <= 1; oy++) {
          for (let ox = -1; ox <= 1; ox++) {
            const xx = x + ox, yy = y + oy;
            if (xx < 0 || yy < 0 || xx >= W || yy >= H) continue;
            const j = yy * W + xx;
            if (!written[j]) continue;
            for (let m = 0; m < outs.length; m++) { const d = outs[m]!.data; sum[m * 3] += d[j * 4]!; sum[m * 3 + 1] += d[j * 4 + 1]!; sum[m * 3 + 2] += d[j * 4 + 2]!; }
            c++;
          }
        }
        if (c) {
          for (let m = 0; m < outs.length; m++) { const d = outs[m]!.data; d[i * 4] = sum[m * 3]! / c; d[i * 4 + 1] = sum[m * 3 + 1]! / c; d[i * 4 + 2] = sum[m * 3 + 2]! / c; d[i * 4 + 3] = 255; }
          next[i] = 1;
        }
      }
    }
    written.set(next);
  }
  // 안 쓰인 곳은 중간 회색 — 검정이면 밉맵 경계가 어두워진다. ORM 은 (AO 1, 러프 0.9, 메탈 0), 노멀은 평평(128,128,255).
  const fillOf = (k: string): [number, number, number] => (k === 'orm' ? [255, 230, 0] : k === 'normal' ? [128, 128, 255] : k === 'emissive' ? [0, 0, 0] : [128, 128, 128]);
  const fills = [fillOf('albedo'), ...extraKeys.map(fillOf)];
  for (let i = 0; i < W * H; i++) {
    if (written[i]) continue;
    for (let m = 0; m < outs.length; m++) { const d = outs[m]!.data, f = fills[m]!; d[i * 4] = f[0]; d[i * 4 + 1] = f[1]; d[i * 4 + 2] = f[2]; d[i * 4 + 3] = 0; }
  }

  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(newPos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(newNrm, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(newUv, 2));
  g.setAttribute('aSmoothN', new THREE.Float32BufferAttribute(newSmooth, 3));
  g.setAttribute('aVisA', new THREE.Float32BufferAttribute(newVisA, 4));
  g.setAttribute('aVisB', new THREE.Float32BufferAttribute(newVisB, 4));
  g.setAttribute('aShell', new THREE.Float32BufferAttribute(new Float32Array(newPos.length / 3), 1));
  g.setIndex(new THREE.BufferAttribute(newIndex, 1));
  g.computeBoundingBox();
  g.computeBoundingSphere();
  return { geometry: g, albedo: out, extra: extraOut, vertexCount: newPos.length / 3, chartShare };
}
